/**
 * Authentication Service
 * 
 * Handles all authentication-related API calls
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';
import { checkRateLimit, recordOtpRequest } from '../utils/rateLimiter';
import authStorage from '../utils/authStorage';
import CryptoJS from 'crypto-js';

// Secret key from backend .env (securely provided for this environment)
const APP_KEY_BASE64 = "XYWj5jsNuJAcUmnXf7+/2upAH044YEJHuLn/e8INxFY=";
const API_SECRET = CryptoJS.enc.Base64.parse(APP_KEY_BASE64);

const authService = {
  /**
   * Verify response integrity using HMAC-SHA256
   * @param {Object} responseData - The full response object { data, signature, timestamp, message }
   * @returns {boolean} True if signature is valid
   */
  verifyResponseIntegrity(responseData) {
    try {
      if (!responseData || !responseData.signature || !responseData.timestamp) {
        console.warn('Response missing security headers');
        return false; // Fail safe
      }

      // 1. Verify timestamp (prevent replay attacks - 5 min window)
      const now = Math.floor(Date.now() / 1000);
      const tolerance = 300; // 5 minutes
      if (Math.abs(now - responseData.timestamp) > tolerance) {
        console.error('Response timestamp expired or future');
        return false;
      }

      // 2. Re-create signature
      // We must match PHP's json_encode behavior exactly. 
      // For simple objects, JSON.stringify usually works. 
      // If data is null/empty array, handle carefully.
      const dataPayload = responseData.data;

      // Handle the case where PHP json_encode([]) becomes [] but {} becomes {}
      // In our helper, we pass $data. 
      let payloadString = JSON.stringify(dataPayload);

      // PHP json_encode with JSON_UNESCAPED_SLASHES | JSON_UNESCAPED_UNICODE
      // JSON.stringify by default doesn't escape slashes, but escapes unicode.
      // We might need adjustment if payload has special chars.
      // For now, assume standard usage.

      const message = payloadString + '|' + responseData.timestamp;

      const generatedSignature = CryptoJS.HmacSHA256(message, API_SECRET).toString(CryptoJS.enc.Hex);

      if (generatedSignature !== responseData.signature) {
        console.error('Signature mismatch', {
          expected: responseData.signature,
          generated: generatedSignature,
          messageUsed: message
        });
        return false;
      }

      return true;
    } catch (e) {
      console.error('Integrity check error', e);
      return false;
    }
  },
  /**
   * Check if user is barred
   * @param {Object} userData - User data object
   * @returns {boolean} True if user is barred
   */
  isUserBarred(userData) {
    if (!userData) return false;

    // Check various possible barred status fields
    return userData.is_barred === true ||
      userData.barred === true ||
      userData.status === 'barred' ||
      userData.status === 'blocked' ||
      userData.status === 'suspended' ||
      userData.user_status === 'barred' ||
      userData.user_status === 'blocked' ||
      userData.user_status === 'suspended';
  },

  /**
   * Get Captcha
   * @returns {Promise<Object>} { key, image }
   */
  async getCaptcha() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.auth.captcha);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to load captcha' });
    }
  },

  /**
   * Login user
   * @param {Object} credentials - Login credentials (username, password, role)
   * @returns {Promise<Object>} User data and token
   */
  async login(credentials) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.login, credentials);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        throw { response: { data: { message: 'Security check failed. Invalid response signature.' } } };
      }

      const responseData = response.data.data || {}; // Extract actual data

      // Extract token (without Bearer prefix) - Laravel Sanctum returns token directly
      const token = responseData.token || responseData.access_token;

      if (token) {
        // Regenerate session before storing new credentials (CWE-384 mitigation)
        this.regenerateSession();
        // Store only the token value in cookies (without "Bearer " prefix)
        authStorage.setItem('auth_token', token);
      }

      // Store user data (handle both 'user' and 'superadmin' fields)
      const userData = responseData.user || responseData.superadmin || responseData.data;
      if (userData) {
        localStorage.setItem('user_data', JSON.stringify(userData));

        // Check if user is barred
        const isBarred = this.isUserBarred(userData);
        if (isBarred) {
          return {
            success: false,
            message: 'Your account has been barred. Please contact administrator.',
            data: response.data,
            user: userData,
            isBarred: true,
          };
        }
      }

      return {
        success: true,
        data: response.data,
        token: token,
        user: userData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Login failed' });
    }
  },


  /**
   * Send OTP to email or phone
   * @param {Object} data - { type: 'email'|'phone', value: string, captcha: string, captcha_key: string }
   * @returns {Promise<Object>}
   */
  async sendOtp(data) {
    try {
      // Extract identifier (email or phone) from data
      const identifier = data.value || data.login || data.email || data.phone;

      // Allow API call to proceed (don't block at frontend)
      const response = await apiClient.post(API_ENDPOINTS.auth.sendOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Invalid response signature'
        };
      }

      // Check Identity Binding
      if (response.data.data && response.data.data.login && response.data.data.login !== identifier) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Response identity mismatch'
        };
      }

      // Record successful OTP request
      if (response.data) {
        recordOtpRequest(identifier);
      }

      return {
        success: true,
        data: response.data.data, // Return the inner data
      };
    } catch (error) {
      const identifier = data.value || data.login || data.email || data.phone;

      // Get API error directly from response (prioritize error field)
      const apiErrorFromResponse = error.response?.data?.data?.error || error.response?.data?.error || error.response?.data?.message || '';
      const apiErrorLower = apiErrorFromResponse.toLowerCase();

      // Check if API returned a proper OTP limit error
      const isProperOtpLimitError =
        (apiErrorLower.includes('too many') && apiErrorLower.includes('otp')) ||
        (apiErrorLower.includes('max') && (apiErrorLower.includes('otp') || apiErrorLower.includes('limit'))) ||
        apiErrorLower.includes('rate limit') ||
        (apiErrorLower.includes('otp') && (apiErrorLower.includes('limit') || apiErrorLower.includes('many') || apiErrorLower.includes('exceeded'))) ||
        error.response?.status === 429;

      // If API returned proper error, always use it (regardless of frontend limit)
      if (isProperOtpLimitError && apiErrorFromResponse) {
        return {
          success: false,
          error: apiErrorFromResponse,
          message: apiErrorFromResponse, // Use prioritized message
          status: error.response?.status
        };
      }

      // If API didn't return proper error, check frontend limit
      const rateLimitCheck = checkRateLimit(identifier);
      if (!rateLimitCheck.allowed) {
        // API didn't return proper error but we've exceeded frontend limit, show custom error
        const errorResponse = handleApiError(error, { defaultMessage: 'Failed to send OTP' });
        return {
          ...errorResponse,
          error: rateLimitCheck.message || 'Maximum 5 OTP requests allowed per 15 minutes. Please try again later.',
          rateLimitExceeded: true,
          resetTime: rateLimitCheck.resetTime
        };
      }

      // Otherwise, show API error
      const errorResponse = handleApiError(error, { defaultMessage: 'Failed to send OTP' });
      return {
        ...errorResponse,
        error: errorResponse.error || errorResponse.message || 'Failed to send OTP'
      };
    }
  },

  /**
   * Send OTP for authority validation (alias for authoritySendOtp)
   * @param {Object} data - { login: string, type: 'email'|'phone' }
   * @returns {Promise<Object>}
   */
  async validateSendOtp(data) {
    // This is an alias for authoritySendOtp
    return this.authoritySendOtp(data);
  },

  /**
   * Send OTP for authority validation
   * @param {Object} data - { login: string, type: 'email'|'phone', captcha: string, captcha_key: string }
   * @returns {Promise<Object>}
   */
  async authoritySendOtp(data) {
    try {
      // Extract identifier (email or phone) from data
      const identifier = data.login || data.value || data.email || data.phone;

      // Allow API call to proceed (don't block at frontend)
      const response = await apiClient.post(API_ENDPOINTS.auth.authoritySendOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Invalid response signature'
        };
      }

      // Check Identity Binding
      if (response.data.data && response.data.data.login && response.data.data.login !== identifier) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Response identity mismatch'
        };
      }

      const responseData = response.data.data || {};

      // Record successful OTP request
      if (response.data) {
        recordOtpRequest(identifier);
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      const errorResponse = handleApiError(error, { defaultMessage: 'Failed to send OTP for authority validation' });
      // Extract granular error from signed response data if available
      const detailedError = error.response?.data?.data?.error;
      if (detailedError) {
        errorResponse.error = detailedError;
        errorResponse.message = detailedError;
      }
      const identifier = data.login || data.value || data.email || data.phone;

      // Check if we've hit the frontend limit and API didn't return proper error
      const rateLimitCheck = checkRateLimit(identifier);
      const apiError = errorResponse.error || errorResponse.message || '';
      const apiErrorLower = apiError.toLowerCase();

      // Check if API returned a proper OTP limit error
      const isProperOtpLimitError =
        apiErrorLower.includes('too many') && apiErrorLower.includes('otp') ||
        apiErrorLower.includes('max') && (apiErrorLower.includes('otp') || apiErrorLower.includes('limit')) ||
        apiErrorLower.includes('rate limit') ||
        apiErrorLower.includes('otp') && (apiErrorLower.includes('limit') || apiErrorLower.includes('many') || apiErrorLower.includes('exceeded')) ||
        error.response?.status === 429;

      // If API returned proper error, use it; otherwise if we've exceeded frontend limit, show custom error
      if (isProperOtpLimitError) {
        // API returned proper error, use it
        return {
          ...errorResponse,
          error: errorResponse.error || errorResponse.message || 'Failed to send OTP'
        };
      } else if (!rateLimitCheck.allowed) {
        // API didn't return proper error but we've exceeded frontend limit, show custom error
        return {
          ...errorResponse,
          error: rateLimitCheck.message || 'Maximum 5 OTP requests allowed per 15 minutes. Please try again later.',
          rateLimitExceeded: true,
          resetTime: rateLimitCheck.resetTime
        };
      }

      return {
        ...errorResponse,
        error: errorResponse.error || errorResponse.message || 'Failed to send OTP for authority validation'
      };
    }
  },

  /**
   * Verify OTP for authority validation
   * @param {Object} data - { login: string, otp: string, otp_token: string, type: 'email'|'phone' }
   * @returns {Promise<Object>}
   */
  async authorityVerifyOtp(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.authorityVerifyOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        throw { response: { data: { message: 'Security check failed. Invalid response signature.' } } };
      }

      const responseData = response.data.data || {};

      // Regenerate session on successful OTP verification (CWE-384 mitigation)
      const token = responseData.token || responseData.access_token;
      if (token) {
        this.regenerateSession();
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to verify OTP for authority validation' });
    }
  },

  /**
   * Send OTP for CVO validation
   * @param {Object} data - { login: string, captcha: string, captcha_key: string }
   * @returns {Promise<Object>}
   */
  async cvoSendOtp(data) {
    try {
      // Extract identifier (email or phone) from data
      const identifier = data.login || data.value || data.email || data.phone;

      // Allow API call to proceed (don't block at frontend)
      const response = await apiClient.post(API_ENDPOINTS.auth.cvoSendOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Invalid response signature'
        };
      }

      // Check Identity Binding
      if (response.data.data && response.data.data.login && response.data.data.login !== identifier) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Response identity mismatch'
        };
      }

      const responseData = response.data.data || {};

      // Record successful OTP request
      if (response.data) {
        recordOtpRequest(identifier);
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      const errorResponse = handleApiError(error, { defaultMessage: 'Failed to send OTP for CVO validation' });
      // Extract granular error from signed response data if available
      const detailedError = error.response?.data?.data?.error;
      if (detailedError) {
        errorResponse.error = detailedError;
        errorResponse.message = detailedError;
      }
      const identifier = data.login || data.value || data.email || data.phone;

      // Check if we've hit the frontend limit and API didn't return proper error
      const rateLimitCheck = checkRateLimit(identifier);
      const apiError = errorResponse.error || errorResponse.message || '';
      const apiErrorLower = apiError.toLowerCase();

      // Check if API returned a proper OTP limit error
      const isProperOtpLimitError =
        apiErrorLower.includes('too many') && apiErrorLower.includes('otp') ||
        apiErrorLower.includes('max') && (apiErrorLower.includes('otp') || apiErrorLower.includes('limit')) ||
        apiErrorLower.includes('rate limit') ||
        apiErrorLower.includes('otp') && (apiErrorLower.includes('limit') || apiErrorLower.includes('many') || apiErrorLower.includes('exceeded')) ||
        error.response?.status === 429;

      // If API returned proper error, use it; otherwise if we've exceeded frontend limit, show custom error
      if (isProperOtpLimitError) {
        // API returned proper error, use it
        return {
          ...errorResponse,
          error: errorResponse.error || errorResponse.message || 'Failed to send OTP'
        };
      } else if (!rateLimitCheck.allowed) {
        // API didn't return proper error but we've exceeded frontend limit, show custom error
        return {
          ...errorResponse,
          error: rateLimitCheck.message || 'Maximum 5 OTP requests allowed per 15 minutes. Please try again later.',
          rateLimitExceeded: true,
          resetTime: rateLimitCheck.resetTime
        };
      }

      return {
        ...errorResponse,
        error: errorResponse.error || errorResponse.message || 'Failed to send OTP for CVO validation'
      };
    }
  },

  /**
   * Verify OTP for CVO validation
   * @param {Object} data - { login: string, otp: string, otp_token: string }
   * @returns {Promise<Object>}
   */
  async cvoVerifyOtp(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.cvoVerifyOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        throw { response: { data: { message: 'Security check failed. Invalid response signature.' } } };
      }

      const responseData = response.data.data || {};

      // Regenerate session on successful OTP verification (CWE-384 mitigation)
      const token = responseData.token || responseData.access_token;
      if (token) {
        this.regenerateSession();
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to verify OTP for CVO validation' });
    }
  },

  /**
   * Send OTP for user validation
   * @param {Object} data - { login: string, type: 'email'|'phone' }
   * @returns {Promise<Object>}
   */
  async userSendValidationOtp(data) {
    try {
      // Extract identifier (email or phone) from data
      const identifier = data.login || data.value || data.email || data.phone;

      // Allow API call to proceed (don't block at frontend)
      const requestData = {
        ...data,
        type: 'user'
      };
      const response = await apiClient.post(API_ENDPOINTS.auth.userSendValidationOtp, requestData);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Invalid response signature'
        };
      }

      // Check Identity Binding
      if (response.data.data && response.data.data.login && response.data.data.login !== identifier) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Response identity mismatch'
        };
      }

      const responseData = response.data.data || {};

      // Record successful OTP request
      if (response.data) {
        recordOtpRequest(identifier);
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      const errorResponse = handleApiError(error, { defaultMessage: 'Failed to send OTP for user validation' });
      // Extract granular error from signed response data if available
      const detailedError = error.response?.data?.data?.error;
      if (detailedError) {
        errorResponse.error = detailedError;
        errorResponse.message = detailedError;
      }
      const identifier = data.login || data.value || data.email || data.phone;

      // Check if we've hit the frontend limit and API didn't return proper error
      const rateLimitCheck = checkRateLimit(identifier);
      const apiError = errorResponse.error || errorResponse.message || '';
      const apiErrorLower = apiError.toLowerCase();

      // Check if API returned a proper OTP limit error
      const isProperOtpLimitError =
        apiErrorLower.includes('too many') && apiErrorLower.includes('otp') ||
        apiErrorLower.includes('max') && (apiErrorLower.includes('otp') || apiErrorLower.includes('limit')) ||
        apiErrorLower.includes('rate limit') ||
        apiErrorLower.includes('otp') && (apiErrorLower.includes('limit') || apiErrorLower.includes('many') || apiErrorLower.includes('exceeded')) ||
        error.response?.status === 429;

      // If API returned proper error, use it; otherwise if we've exceeded frontend limit, show custom error
      if (isProperOtpLimitError) {
        // API returned proper error, use it
        return {
          ...errorResponse,
          error: errorResponse.error || errorResponse.message || 'Failed to send OTP'
        };
      } else if (!rateLimitCheck.allowed) {
        // API didn't return proper error but we've exceeded frontend limit, show custom error
        return {
          ...errorResponse,
          error: rateLimitCheck.message || 'Maximum 5 OTP requests allowed per 15 minutes. Please try again later.',
          rateLimitExceeded: true,
          resetTime: rateLimitCheck.resetTime
        };
      }

      return {
        ...errorResponse,
        error: errorResponse.error || errorResponse.message || 'Failed to send OTP for user validation'
      };
    }
  },

  /**
   * Verify OTP for user validation
   * @param {Object} data - { login: string, otp: string, type: 'email'|'phone' }
   * @returns {Promise<Object>}
   */
  async userVerifyValidationOtp(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.userVerifyValidationOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        throw { response: { data: { message: 'Security check failed. Invalid response signature.' } } };
      }

      const responseData = response.data.data || {};

      // Regenerate session on successful OTP verification (CWE-384 mitigation)
      const token = responseData.token || responseData.access_token;
      if (token) {
        this.regenerateSession();
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to verify OTP for user validation' });
    }
  },

  /**
   * Verify OTP
   * @param {Object} data - { type: 'email'|'phone', value: string, otp: string, otp_token: string }
   * @returns {Promise<Object>}
   */
  async verifyOtp(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.verifyOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        throw { response: { data: { message: 'Security check failed. Invalid response signature.' } } };
      }
      const responseData = response.data.data || {};

      // Extract token (without Bearer prefix) - Laravel Sanctum returns token directly
      const token = responseData.token || responseData.access_token;

      if (token) {
        // Regenerate session before storing new credentials (CWE-384 mitigation)
        this.regenerateSession();
        // Store only the token value in cookies (without "Bearer " prefix)
        authStorage.setItem('auth_token', token);
      } else {
        console.error('No token received from verify-otp API');
        return {
          success: false,
          error: 'Authentication failed - no token received',
          message: 'Authentication failed - no token received',
        };
      }

      // Store user data (handle both 'user' and 'superadmin' fields)
      const userData = responseData.user || responseData.superadmin || responseData.data;
      if (userData) {
        localStorage.setItem('user_data', JSON.stringify(userData));

        // Check if user is barred
        const isBarred = this.isUserBarred(userData);
        if (isBarred) {
          return {
            success: false,
            message: 'Your account has been barred. Please contact administrator.',
            data: response.data,
            user: userData,
            isBarred: true,
          };
        }
      }

      return {
        success: true,
        data: response.data,
        token: token,
        user: userData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'OTP verification failed' });
    }
  },

  /**
   * Reset password
   * @param {Object} data - Password reset data
   * @returns {Promise<Object>}
   */
  async resetPassword(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.resetPassword, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Password reset failed' });
    }
  },

  /**
   * Get current user data
   * @returns {Object|null} User data or null
   */
  getCurrentUser() {
    try {
      const userData = localStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    const token = authStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');

    if (!token || !userData) {
      return false;
    }

    // Check if token has expired (if it contains expiry information)
    try {
      const user = JSON.parse(userData);
      // You can add additional token validation logic here if needed
      return true;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  },

  /**
   * Get auth token
   * @returns {string|null}
   */
  getToken() {
    return authStorage.getItem('auth_token');
  },

  /**
   * Register new user
   * @param {Object} userData - { fullname, email, mobile, employee_id?, captcha: string, captcha_key: string }
   * @returns {Promise<Object>}
   */
  async registerUser(userData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.userRegister, userData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'User registration failed' });
    }
  },

  /**
   * Send verification OTP for email/phone
   * @param {Object} data - { login: string }
   * @returns {Promise<Object>}
   */
  async sendVerificationOtp(data) {
    try {
      // Extract identifier (email or phone) from data
      const identifier = data.login || data.value || data.email || data.phone;

      // Allow API call to proceed (don't block at frontend)
      const response = await apiClient.post(API_ENDPOINTS.auth.userSendVerificationOtp, data);

      // Record successful OTP request
      if (response.data) {
        recordOtpRequest(identifier);
      }

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Invalid response signature'
        };
      }

      // Check Identity Binding
      if (response.data.data && response.data.data.login && response.data.data.login !== identifier) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Response identity mismatch'
        };
      }

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      const errorResponse = handleApiError(error, { defaultMessage: 'Failed to send verification OTP' });
      // Extract granular error from signed response data if available
      const detailedError = error.response?.data?.data?.error;
      if (detailedError) {
        errorResponse.error = detailedError;
        errorResponse.message = detailedError;
      }
      const identifier = data.login || data.value || data.email || data.phone;

      // Check if we've hit the frontend limit and API didn't return proper error
      const rateLimitCheck = checkRateLimit(identifier);
      const apiError = errorResponse.error || errorResponse.message || '';
      const apiErrorLower = apiError.toLowerCase();

      // Check if API returned a proper OTP limit error
      const isProperOtpLimitError =
        apiErrorLower.includes('too many') && apiErrorLower.includes('otp') ||
        apiErrorLower.includes('max') && (apiErrorLower.includes('otp') || apiErrorLower.includes('limit')) ||
        apiErrorLower.includes('rate limit') ||
        apiErrorLower.includes('otp') && (apiErrorLower.includes('limit') || apiErrorLower.includes('many') || apiErrorLower.includes('exceeded')) ||
        error.response?.status === 429;

      // If API returned proper error, use it; otherwise if we've exceeded frontend limit, show custom error
      if (isProperOtpLimitError) {
        // API returned proper error, use it
        return {
          ...errorResponse,
          error: errorResponse.error || errorResponse.message || 'Failed to send OTP'
        };
      } else if (!rateLimitCheck.allowed) {
        // API didn't return proper error but we've exceeded frontend limit, show custom error
        return {
          ...errorResponse,
          error: rateLimitCheck.message || 'Maximum 5 OTP requests allowed per 15 minutes. Please try again later.',
          rateLimitExceeded: true,
          resetTime: rateLimitCheck.resetTime
        };
      }

      return {
        ...errorResponse,
        error: errorResponse.error || errorResponse.message || 'Failed to send verification OTP'
      };
    }
  },

  /**
   * Verify verification OTP
   * @param {Object} data - { login: string, otp: string, otp_token: string }
   * @returns {Promise<Object>}
   */
  async verifyVerificationOtp(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.auth.userVerifyVerificationOtp, data);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        throw { response: { data: { message: 'Security check failed. Invalid response signature.' } } };
      }

      const responseData = response.data.data || {};

      // Regenerate session on successful OTP verification (CWE-384 mitigation)
      const token = responseData.token || responseData.access_token;
      if (token) {
        this.regenerateSession();
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'OTP verification failed' });
    }
  },

  /**
   * Send OTP for user login
   * @param {Object} data - { login: string, captcha: string, captcha_key: string }
   * @returns {Promise<Object>}
   */
  async userSendLoginOtp(data) {
    try {
      // Extract identifier (email or phone) from data
      const identifier = data.login || data.value || data.email || data.phone;

      // Allow API call to proceed (don't block at frontend)
      const requestData = {
        ...data,
        type: 'user'
      };
      const response = await apiClient.post(API_ENDPOINTS.auth.userSendOtp, requestData);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Invalid response signature'
        };
      }

      // Check Identity Binding (STRICT)
      // Check Identity Binding (STRICT)
      const responseLogin = response.data.data && response.data.data.login;

      if (!responseLogin) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Invalid response format: Identity missing'
        };
      }

      if (responseLogin !== identifier) {
        return {
          success: false,
          message: 'Security validation failed',
          error: 'Response identity mismatch'
        };
      }


      const responseData = response.data.data || {};

      // Record successful OTP request
      if (response.data) {
        recordOtpRequest(identifier);
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      const errorResponse = handleApiError(error, { defaultMessage: 'Failed to send login OTP' });
      // Extract granular error from signed response data if available
      const detailedError = error.response?.data?.data?.error;
      if (detailedError) {
        errorResponse.error = detailedError;
        errorResponse.message = detailedError;
      }

      const identifier = data.login || data.value || data.email || data.phone;

      // Check if we've hit the frontend limit and API didn't return proper error
      const rateLimitCheck = checkRateLimit(identifier);
      const apiError = errorResponse.error || errorResponse.message || '';
      const apiErrorLower = apiError.toLowerCase();

      // Check if API returned a proper OTP limit error
      const isProperOtpLimitError =
        apiErrorLower.includes('too many') && apiErrorLower.includes('otp') ||
        apiErrorLower.includes('max') && (apiErrorLower.includes('otp') || apiErrorLower.includes('limit')) ||
        apiErrorLower.includes('rate limit') ||
        apiErrorLower.includes('otp') && (apiErrorLower.includes('limit') || apiErrorLower.includes('many') || apiErrorLower.includes('exceeded')) ||
        error.response?.status === 429;

      // If API returned proper error, use it; otherwise if we've exceeded frontend limit, show custom error
      if (isProperOtpLimitError) {
        // API returned proper error, use it
        return {
          ...errorResponse,
          error: errorResponse.error || errorResponse.message || 'Failed to send OTP'
        };
      } else if (!rateLimitCheck.allowed) {
        // API didn't return proper error but we've exceeded frontend limit, show custom error
        return {
          ...errorResponse,
          error: rateLimitCheck.message || 'Maximum 5 OTP requests allowed per 15 minutes. Please try again later.',
          rateLimitExceeded: true,
          resetTime: rateLimitCheck.resetTime
        };
      }

      return {
        ...errorResponse,
        error: errorResponse.error || errorResponse.message || 'Failed to send login OTP'
      };
    }
  },

  /**
   * Verify OTP for user login
   * @param {Object} data - { login: string, otp: string, otp_token: string }
   * @returns {Promise<Object>}
   */
  async userVerifyLoginOtp(data) {
    try {
      const requestData = {
        ...data,
        type: 'user'
      };
      const response = await apiClient.post(API_ENDPOINTS.auth.userVerifyOtp, requestData);

      // Verify integrity
      if (!this.verifyResponseIntegrity(response.data)) {
        throw { response: { data: { message: 'Security check failed. Invalid response signature.' } } };
      }




      const responseData = response.data.data || {};

      // Extract token (without Bearer prefix) - Laravel Sanctum returns token directly
      const token = responseData.token || responseData.access_token;

      if (token) {
        // Regenerate session before storing new credentials (CWE-384 mitigation)
        this.regenerateSession();
        // Store only the token value in cookies (without "Bearer " prefix)
        authStorage.setItem('auth_token', token);
      } else {
        console.error('No token received from user verify-otp API');
        return {
          success: false,
          error: 'Authentication failed - no token received',
          message: 'Authentication failed - no token received',
        };
      }

      // Store user data (handle both 'user' and 'superadmin' fields)
      const userData = responseData.user || responseData.superadmin || responseData.data;
      if (userData) {
        localStorage.setItem('user_data', JSON.stringify(userData));

        // Check if user is barred
        const isBarred = this.isUserBarred(userData);
        if (isBarred) {
          return {
            success: false,
            message: 'Your account has been barred. Please contact administrator.',
            data: response.data,
            user: userData,
            isBarred: true,
          };
        }
      }

      return {
        success: true,
        data: response.data,
        token: token,
        user: userData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Login OTP verification failed' });
    }
  },

  /**
   * Logout user
   * @param {string} role - Optional user role ('cvo' or 'authority')
   * @returns {Promise<Object>}
   */
  async logout(role = null) {
    try {
      // Determine the logout endpoint based on role
      let logoutEndpoint = API_ENDPOINTS.auth.logout;

      if (role) {
        const roleLower = role.toLowerCase();
        if (roleLower === 'cvo') {
          logoutEndpoint = API_ENDPOINTS.auth.cvoLogout;
        } else if (roleLower === 'authority') {
          logoutEndpoint = API_ENDPOINTS.auth.authorityLogout;
        } else if (roleLower === 'user' || roleLower === 'citizen') {
          logoutEndpoint = API_ENDPOINTS.auth.userLogout;
        }
      }

      // Call the appropriate logout endpoint with token in header (handled by interceptor)
      await apiClient.post(logoutEndpoint);

      // Clear stored data
      this.clearAuthData();

      return {
        success: true,
        message: 'Logged out successfully',
      };
    } catch (error) {
      // Clear local data even if API call fails
      this.clearAuthData();

      return handleApiError(error, { defaultMessage: 'Logout failed' });
    }
  },

  /**
   * Get employee details by employment ID
   * @param {string} employmentId - Employee ID
   * @param {string} captcha - CAPTCHA value
   * @param {string} captchaKey - CAPTCHA key
   * @returns {Promise<Object>} Employee details
   */
  async getEmployeeDetail(employmentId, captcha, captchaKey) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.user.employeeDetail, {
        params: { 
          employment_id: employmentId,
          captcha: captcha,
          captcha_key: captchaKey
        }
      });
      // Handle different response structures
      const employeeData = response.data?.data || response.data || {};
      return {
        success: true,
        data: employeeData,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch employee details' });
    }
  },

  /**
   * Clear all authentication data from localStorage, sessionStorage, and cookies
   * This is critical for session fixation mitigation (CWE-384)
   * @returns {void}
   */
  clearAuthData() {
    // Clear auth token from cookies
    authStorage.removeItem('auth_token');

    // Clear user data and other keys from localStorage
    localStorage.removeItem('user_data');
    localStorage.removeItem('vigilance_user_data');
    localStorage.removeItem('cvo_dashboard_data');
    localStorage.removeItem('otp_token');
    localStorage.removeItem('authority_token');
    localStorage.removeItem('cvo_token');
    localStorage.removeItem('user_token');

    // Clear Redux Persist state to prevent rehydration of sensitive data
    localStorage.removeItem('persist:vigilance-root');

    // Clear session storage completely for session fixation mitigation
    try {
      sessionStorage.clear();
    } catch (e) {
      // In case sessionStorage is not available (e.g., in private browsing)
      console.warn('Could not clear sessionStorage:', e);
    }

    // Clear auth-related cookies
    this.clearAuthCookies();
  },

  /**
   * Clear all authentication-related cookies
   * Helps prevent session fixation by removing any persistent session identifiers
   * @returns {void}
   */
  clearAuthCookies() {
    const cookiesToClear = [
      'laravel_session',
      'XSRF-TOKEN',
      'vigilance_session',
      'auth_session',
      'remember_token',
      'session_id'
    ];

    cookiesToClear.forEach(cookieName => {
      // Clear cookie for current path
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      // Clear cookie for root path
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      // Clear with secure flag
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure;`;
    });
  },

  /**
   * Regenerate session on the frontend side
   * Called after successful authentication to ensure a fresh session state
   * This complements the backend's Session::regenerate() call
   * @returns {void}
   */
  regenerateSession() {
    // Clear any existing session-related data before storing new credentials
    try {
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear sessionStorage during regeneration:', e);
    }

    // Clear old auth cookies to ensure fresh session
    this.clearAuthCookies();

    // Generate a new client-side session marker (for tracking purposes)
    const newSessionMarker = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    try {
      sessionStorage.setItem('client_session_marker', newSessionMarker);
    } catch (e) {
      console.warn('Could not set session marker:', e);
    }
  },

};

export default authService;

